import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Flag, Star, AlertCircle } from 'lucide-react';
import { GlossaryTerm, GlossarySourceCategory } from '../../types/glossary';
import { glossaryService } from '../../services/glossaryService';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';
import { useAuth } from '../../hooks/useAuth';
import { TermOfTheDay } from './TermOfTheDay';
import { ChromeExtension } from './ChromeExtension';
import { CategorySelector } from './CategorySelector';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { toast } from '../ui/use-toast';

/**
 * Main component for displaying the glossary terms list
 */
export const GlossaryList: React.FC = () => {
  const { user } = useAuth();
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<GlossaryTerm[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTerms, setExpandedTerms] = useState<Record<string, boolean>>({});
  const [favoritedTerms, setFavoritedTerms] = useState<Record<string, boolean>>({});
  const [totalTerms, setTotalTerms] = useState(0);
  const [categories, setCategories] = useState<GlossarySourceCategory[]>([]);

// Fetch categories for name mapping
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allCategories = await glossaryCategoryService.getActiveCategories();
        setCategories(allCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);
  // Fetch all glossary terms
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const allTerms = await glossaryService.getGlossaryTerms();
        setTerms(allTerms);
        setFilteredTerms(allTerms);
        setTotalTerms(allTerms.length);
        setError(null);
      } catch (err) {
        console.error('Error fetching glossary terms:', err);
        setError('Failed to load glossary terms');
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // Check which terms are favorited
  useEffect(() => {
    const checkFavoritedTerms = async () => {
      if (!user || terms.length === 0) return;

      try {
        // Get all favorites for the user
        const favorites = await glossaryService.getFavorites(user.uid);
        
        // Create a map of term IDs to favorited status
        const favoritedMap: Record<string, boolean> = {};
        terms.forEach(term => {
          // Check if this term ID is in the favorites
          favoritedMap[term.id] = favorites.some(fav => fav.termId === term.id);
        });
        
        console.log('Favorited terms:', favoritedMap);
        setFavoritedTerms(favoritedMap);
      } catch (err) {
        console.error('Error checking favorited terms:', err);
      }
    };

    checkFavoritedTerms();
  }, [user, terms]);

  // Filter terms based on search query and selected category
  useEffect(() => {
    let filtered = terms;

    // Filter by category if one is selected
    if (selectedCategoryId) {
      filtered = filtered.filter(term => term.categoryId === selectedCategoryId);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(term =>
        term.term.toLowerCase().includes(query) ||
        term.content.toLowerCase().includes(query)
      );
    }
    
    setFilteredTerms(filtered);
  }, [searchQuery, selectedCategoryId, terms]);

  // Toggle term expansion
  const toggleTermExpansion = (termId: string) => {
    setExpandedTerms(prev => ({
      ...prev,
      [termId]: !prev[termId]
    }));
  };

  // Toggle term favorite status
  const toggleFavorite = async (termId: string) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to manage favorites",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if the term is already favorited
      const isFavorited = favoritedTerms[termId];
      
      if (isFavorited) {
        // Remove from favorites
        await glossaryService.removeFavorite(termId, user.uid);
        
        // Update favorited terms state immediately
        setFavoritedTerms(prev => ({
          ...prev,
          [termId]: false
        }));
        
        console.log(`Term ${termId} removed from favorites`);
        
        toast({
          title: "Removed from Favorites",
          description: "Term has been removed from your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      } else {
        // Get user's categories
        const categories = await glossaryService.getCategories(user.uid);
        
        // Find or create default category
        let defaultCategory = categories.find(cat => cat.name === 'My Favorites');
        
        if (!defaultCategory) {
          const categoryId = await glossaryService.createCategory(
            'My Favorites',
            user.uid,
            user.displayName || 'Unknown User',
            user.photoURL || undefined
          );
          
          // Fetch categories again to get the new one
          const updatedCategories = await glossaryService.getCategories(user.uid);
          defaultCategory = updatedCategories.find(cat => cat.id === categoryId);
          
          if (!defaultCategory) {
            throw new Error('Failed to create default category');
          }
        }
        
        // Add to favorites
        await glossaryService.addToFavorites(termId, user.uid, defaultCategory.id);
        
        // Update favorited terms state immediately
        setFavoritedTerms(prev => ({
          ...prev,
          [termId]: true
        }));
        
        console.log(`Term ${termId} added to favorites`);
        
        toast({
          title: "Added to Favorites",
          description: "Term has been added to your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700",
        duration: 2000,
      });
    }
  };
// Get category name from ID
  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return 'General';
    
    // First try to use the categoryName if it exists
    const term = terms.find(t => t.categoryId === categoryId);
    if (term?.categoryName) return term.categoryName;
    
    // Otherwise look up in categories array
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || categoryId;
  };

  return (
    <div className="px-4 md:px-8">
      {/* Featured components in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <TermOfTheDay />
        <ChromeExtension />
      </div>
      
      {/* Glossary Terms List */}
      <div className="mb-4">
        {/* Category Selector and Search bar on same line */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <CategorySelector
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={setSelectedCategoryId}
              />
              <div className="text-gray-800 dark:text-gray-300">
                {selectedCategoryId
                  ? `${filteredTerms.length} ${getCategoryName(selectedCategoryId)} terms`
                  : `${totalTerms} total terms available`
                }
              </div>
            </div>
            
            <div className="relative flex-1 md:max-w-md">
              <Input
                type="text"
                placeholder="Search glossary terms..."
                className="pr-10 glossary-search bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                style={{
                  border: '2px solid var(--glossary-border)',
                  boxShadow: '0 0 0 1px rgba(99, 102, 241, 0.1)'
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  onClick={() => {/* Search functionality already handled by onChange */}}
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Error state */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="border border-gray-800 rounded-lg overflow-hidden">
                <div className="bg-gray-800 p-4">
                  <Skeleton className="h-6 w-1/3" />
                </div>
                <div className="p-4">
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* No results */}
        {!loading && filteredTerms.length === 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
            <p className="text-gray-400">
              {searchQuery 
                ? `No terms found matching "${searchQuery}"`
                : "No glossary terms available"}
            </p>
          </div>
        )}
        
        {/* Terms list */}
        {!loading && filteredTerms.length > 0 && (
          <div className="space-y-4">
            {filteredTerms.map(term => (
              <div 
                key={term.id} 
                className="border border-gray-700 rounded-lg overflow-hidden"
              >
                <div 
                  className="bg-indigo-600/80 p-3 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleTermExpansion(term.id)}
                >
                  <h3 className="text-lg font-semibold text-white">{term.term}</h3>
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(term.id);
                      }}
                      className={`text-white hover:bg-indigo-700 ${favoritedTerms[term.id] ? 'bg-indigo-800' : ''}`}
                    >
                      <Star
                        className={`h-5 w-5 ${favoritedTerms[term.id] ? 'text-yellow-400 fill-yellow-400' : ''}`}
                      />
                    </Button>
                    <ChevronDown 
                      className={`h-5 w-5 text-white transition-transform ${
                        expandedTerms[term.id] ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </div>
                </div>
                
                {expandedTerms[term.id] && (
                  <div className="p-3" style={{ backgroundColor: 'var(--glossary-term-content-bg)' }}>
                    <div
                      className="prose prose-invert max-w-none prose-sm"
                      dangerouslySetInnerHTML={{ __html: term.content.replace(/<\/p><p>/g, '</p><br><p>') }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};